import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  type IAdobeCredentials,
  type IAnalyticsIntegrationResponse,
  IntegrationName,
  ProductLine,
} from '@kouto/types';
import {
  AnalyticsClient,
  type AnalyticsConfig,
  IAnalyticsDataPayload,
} from 'features/analytics/types/analytics';
import { debug } from 'utils/debug';

interface IAdobeConfig extends AnalyticsConfig {
  credential?: IAdobeCredentials;
}

type AdobePageMapping = {
  pageDetail1: string;
  useSegmentation: boolean;
};

/* 
  This integration is currently designed with Hilton's specs in mind.
  Hilton requested that all analytics map to the pageDetails1 page-detail bucket
  within Adobe's digitalData object. If other clients have different requirements,
  we will need to refactor this mapping to support other page-detail buckets.
*/
const PAGE_LOAD_MAPPING: Partial<Record<ANALYTICS_EVENT, AdobePageMapping>> = {
  [ANALYTICS_EVENT.VIEW_HOME]: { pageDetail1: '', useSegmentation: false },
  [ANALYTICS_EVENT.VIEW_ACTIVITIES]: {
    pageDetail1: '',
    useSegmentation: false,
  },
  [ANALYTICS_EVENT.VIEW_EXPERIENCE_DETAIL]: {
    pageDetail1: 'experience_details',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_COLLECTION]: {
    pageDetail1: 'resource_collection',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_EVENTS]: {
    pageDetail1: 'event_details',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_CALENDAR]: {
    pageDetail1: 'tickets_selection',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_PARTICIPANT_INFO]: {
    pageDetail1: 'participant_details',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_ADDONS]: {
    pageDetail1: 'addons',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_CHECKOUT]: {
    pageDetail1: 'checkout_payment',
    useSegmentation: true,
  },
  [ANALYTICS_EVENT.VIEW_BOOKING_CONFIRMATION]: {
    pageDetail1: 'confirmation',
    useSegmentation: true,
  },
};

type AdobeClickMapping = {
  clickID: (payload: IAnalyticsDataPayload) => string;
};

const CLICK_MAPPING: Partial<Record<ANALYTICS_EVENT, AdobeClickMapping>> = {
  [ANALYTICS_EVENT.CLICK_FILTER]: {
    clickID: (payload) => {
      const name = payload[ANALYTICS_PROPERTY.FilterOptions] ?? '';
      return `activity_filter_${slugify(name)}`;
    },
  },
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

type AdobeErrorMapping = {
  action: string;
};

const ERROR_MAPPING: Partial<Record<ANALYTICS_EVENT, AdobeErrorMapping>> = {
  [ANALYTICS_EVENT.PAGE_STOP_ERROR]: { action: 'page_stop' },
  [ANALYTICS_EVENT.FORM_VALIDATION_ERROR]: { action: 'form_validation' },
};

const PRIMARY_CATEGORY = 'Property';
const PAGE_TYPE = 'activities';
const PROPERTY_PAGE_DCR = 'property_page';
const PROPERTY_CLICK_DCR = 'property_click';
const GLOBAL_CLICK_DCR = 'global_click';

export class AdobeLaunchClient extends AnalyticsClient {
  private readonly integration!: IAdobeConfig;

  readonly provider = IntegrationName.ADOBE_ANALYTICS;

  private lastSentDigest = '';

  private lastSentErrorDigest = '';

  constructor(config: IAnalyticsIntegrationResponse) {
    super();
    this.integration = config as IAdobeConfig;
  }

  init() {
    return this.initialize();
  }

  hasEvent(eventName: ANALYTICS_EVENT) {
    if (
      !(eventName in PAGE_LOAD_MAPPING) &&
      !(eventName in CLICK_MAPPING) &&
      !(eventName in ERROR_MAPPING)
    ) {
      return false;
    }

    const explicit = this.integration?.config?.events?.[eventName]?.enabled;
    return explicit !== false;
  }

  // eslint-disable-next-line class-methods-use-this
  isAvailable() {
    return (
      typeof window._satellite?.track === 'function' &&
      typeof window.digitalData === 'object' &&
      window.digitalData !== null
    );
  }

  private async initialize() {
    if (this.isAvailable()) {
      this.ready = true;
      this.flushBuffer();
      return;
    }

    const res = this.integration.config?.initializeSdk
      ? await this.addAdobeLaunchScript()
      : await this.runWhenAvailable();

    if (!res) {
      return;
    }

    this.ready = true;
    this.flushBuffer();
  }

  private flushBuffer() {
    this.events.forEach((event) =>
      this.dispatch(event.eventName, event.payload),
    );
  }

  private dispatch(eventName: ANALYTICS_EVENT, payload: IAnalyticsDataPayload) {
    if (eventName in PAGE_LOAD_MAPPING) {
      this.emit(eventName, payload);
      return;
    }
    if (eventName in CLICK_MAPPING) {
      this.emitClick(eventName, payload);
      return;
    }
    if (eventName in ERROR_MAPPING) {
      this.emitError(eventName, payload);
    }
  }

  private addAdobeLaunchScript(): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.integration.credential?.embedUrl) {
        resolve(false);
        return;
      }

      // Double-load guard. If the host page already loads Launch, do not
      // inject a second copy — the later script clobbers `_satellite` and
      // can cause duplicate beacons.
      if (typeof window._satellite?.track === 'function') {
        resolve(true);
        return;
      }

      // Seed digitalData synchronously BEFORE Launch boots. Rules tied to
      // Library Loaded / Page Top evaluate at boot time; if our paths are
      // missing, those rules read undefined.
      // https://experienceleague.adobe.com/en/docs/experience-platform/tags/client-side/asynchronous-deployment
      this.seedDigitalData();

      const el = document.createElement('script');
      el.src = this.integration.credential.embedUrl;
      el.type = 'text/javascript';
      el.async = true;

      const lastScript = document.getElementsByTagName('script')[0];
      el.onload = async () => {
        try {
          const res = await this.runWhenAvailable();
          resolve(res);
        } catch {
          resolve(false);
        }
      };

      lastScript.parentNode?.insertBefore(el, lastScript);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private seedDigitalData() {
    window.digitalData = {
      ...window.digitalData,
      global: {
        ...window.digitalData?.global,
        page: { ...window.digitalData?.global?.page },
      },
      universal: { ...window.digitalData?.universal },
    };
  }

  private emit(
    incomingEventName: ANALYTICS_EVENT,
    payload: IAnalyticsDataPayload,
  ) {
    if (!this.hasEvent(incomingEventName)) {
      return;
    }
    if (!this.isAvailable()) {
      return;
    }

    const eventName =
      incomingEventName === ANALYTICS_EVENT.VIEW_EXPERIENCE_DETAIL &&
      payload[ANALYTICS_PROPERTY.ExperienceProduct] === ProductLine.ACTIVATE
        ? ANALYTICS_EVENT.VIEW_EVENTS
        : incomingEventName;

    const mapping = PAGE_LOAD_MAPPING[eventName];
    if (!mapping) {
      return;
    }

    const segmentation = mapping.useSegmentation
      ? payload[ANALYTICS_PROPERTY.ExperienceTitle] ?? ''
      : '';

    const { pageDetail1 } = mapping;

    window.digitalData = {
      ...window.digitalData,
      global: {
        ...window.digitalData?.global,
        page: {
          ...window.digitalData?.global?.page,
          primaryCategory: PRIMARY_CATEGORY,
          pageType: PAGE_TYPE,
          pageDetail1,
        },
      },
      universal: {
        ...window.digitalData?.universal,
        segmentation,
      },
    };

    const digest = `${eventName}|${pageDetail1}|${segmentation}`;
    if (digest === this.lastSentDigest) {
      debug('log', 'Duplicate adobe event detected, skipping send', {
        eventName,
      });
      return;
    }
    this.lastSentDigest = digest;

    // Pass the same payload via `detail` so Hilton's rules can read it
    // through `event.detail.*` without depending on the digitalData merge
    // race-free; the digitalData mutation above remains for rules that read
    // from data elements pointing at those paths.
    const detail = {
      page: {
        primaryCategory: PRIMARY_CATEGORY,
        pageType: PAGE_TYPE,
        pageDetail1,
      },
      segmentation,
    };

    debug('log', IntegrationName.ADOBE_ANALYTICS, {
      type: 'property_page fired',
      eventName,
      detail,
    });

    window._satellite?.track(PROPERTY_PAGE_DCR, detail);
  }

  private emitClick(
    eventName: ANALYTICS_EVENT,
    payload: IAnalyticsDataPayload,
  ) {
    if (!this.hasEvent(eventName)) {
      return;
    }
    if (!this.isAvailable()) {
      return;
    }

    const mapping = CLICK_MAPPING[eventName];
    if (!mapping) {
      return;
    }

    const clickID = mapping.clickID(payload);

    const existingClick =
      (window.digitalData?.click as Record<string, unknown> | undefined) ?? {};

    window.digitalData = {
      ...window.digitalData,
      global: {
        ...window.digitalData?.global,
        page: {
          ...window.digitalData?.global?.page,
          primaryCategory: PRIMARY_CATEGORY,
          pageType: PAGE_TYPE,
        },
      },
      click: {
        ...existingClick,
        clickID,
      },
    };

    // Clicks intentionally bypass the page-load digest dedupe — repeat
    // filter taps must each fire.
    const detail = {
      page: {
        primaryCategory: PRIMARY_CATEGORY,
        pageType: PAGE_TYPE,
      },
      click: {
        clickID,
      },
    };

    debug('log', IntegrationName.ADOBE_ANALYTICS, {
      type: 'property_click fired',
      eventName,
      detail,
    });

    window._satellite?.track(PROPERTY_CLICK_DCR, detail);
  }

  private emitError(
    eventName: ANALYTICS_EVENT,
    payload: IAnalyticsDataPayload,
  ) {
    if (!this.hasEvent(eventName)) {
      return;
    }
    if (!this.isAvailable()) {
      return;
    }

    const mapping = ERROR_MAPPING[eventName];
    if (!mapping) {
      return;
    }

    const code = payload[ANALYTICS_PROPERTY.ErrorCode] ?? '';
    const message = payload[ANALYTICS_PROPERTY.ErrorMessage] ?? '';

    const errorDigest = `${eventName}|${mapping.action}|${code}|${message}`;
    if (errorDigest === this.lastSentErrorDigest) {
      debug('log', 'Duplicate adobe error detected, skipping send', {
        eventName,
      });
      return;
    }
    this.lastSentErrorDigest = errorDigest;

    const existingError =
      (window.digitalData?.error as Record<string, unknown> | undefined) ?? {};

    window.digitalData = {
      ...window.digitalData,
      global: {
        ...window.digitalData?.global,
        page: {
          ...window.digitalData?.global?.page,
          primaryCategory: PRIMARY_CATEGORY,
          pageType: PAGE_TYPE,
        },
      },
      error: {
        ...existingError,
        action: mapping.action,
        code,
        message,
      },
    };

    const detail = {
      page: {
        primaryCategory: PRIMARY_CATEGORY,
        pageType: PAGE_TYPE,
      },
      error: {
        action: mapping.action,
        code,
        message,
      },
    };

    debug('log', IntegrationName.ADOBE_ANALYTICS, {
      type: 'global_click fired',
      eventName,
      detail,
    });

    window._satellite?.track(GLOBAL_CLICK_DCR, detail);
  }

  sendEvent(eventName: ANALYTICS_EVENT, payload: IAnalyticsDataPayload): void {
    if (!this.hasEvent(eventName)) {
      return;
    }
    if (!this.ready) {
      this.events.push({ eventName, payload });
      return;
    }
    this.dispatch(eventName, payload);
  }
}

declare global {
  interface Window {
    _satellite?: {
      track: (eventName: string, data?: Record<string, unknown>) => void;
    };
    digitalData?: {
      global?: {
        page?: {
          primaryCategory?: string;
          pageType?: string;
          pageDetail1?: string;
          [key: string]: unknown;
        };
        [key: string]: unknown;
      };
      universal?: {
        segmentation?: string;
        [key: string]: unknown;
      };
      click?: {
        clickID?: string;
        [key: string]: unknown;
      };
      error?: {
        action?: string;
        code?: string;
        message?: string;
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
  }
}
